import React from 'react';
import Link from 'next/link';
import { ArrowSmDownIcon, StarIcon } from '@heroicons/react/solid';

import { BondTable } from '@components/BondTable';
import { SEO } from '@components/Seo';
import { CarouselTrustBox } from '@components/TrustPilot';
import { getPreviewHubFromDato } from 'data/datocms/getAllStatePages';
import { getSiteData } from '../../../data/bootstrapData';

import { StructuredRenderer } from '@components/StructuredRenderer';

export default function StateHub(props) {
  const { pageData } = props;
  return <>
    <SEO
      title={pageData.metaFields.title}
      description={pageData.metaFields.description}
    />
    <div className="w-full mx-auto font-brand">
      <div className="w-full max-w-screen-lg px-4 mx-auto mt-10 flex flex-col justify-center text-center items-center">
        <h1 className="text-7xl font-bold tracking-tighter max-w-lg">
          <small className="font-medium">{pageData.state}</small>
          <br />
          Contractor Bonds
        </h1>
        <div className="text-2xl mt-7 font-ui max-w-lg leading-normal text-black">
          {pageData.headerBlurb}
        </div>
        <div className="flex flex-row items-center space-x-7 mt-7">
          <Link
            href={
              pageData?.bondFinderConfig
                ? `/find/state/${pageData.slug}`
                : `/find/state/${pageData.slug}/results`
            }
            className="text-white shadow-lg text-xl hover:opacity-50 transition-all bg-blue-surety px-4 py-2 rounded-lg">
            
              Find My Bond
            
          </Link>

          <a
            onClick={() => {
              document
                .getElementById('page-content')
                .scrollIntoView({ behavior: 'smooth' });
            }}
            className="text-xl flex items-center font-medium text-blue-400 cursor-pointer"
          >
            <span>Read More</span>
            <ArrowSmDownIcon className="h-4 w-4 ml-1"></ArrowSmDownIcon>
          </a>
        </div>
        <div className="py-20 border-b w-full">
          <CarouselTrustBox />
        </div>
      </div>

      <div
        id="page-content"
        className="max-w-screen-lg pt-20 mx-auto justify-between w-full flex flex-col md:flex-row items-center md:items-start px-4 "
      >
        <div className="w-full flex flex-col max-w-3xl">
          <div className="sm:text-lg sm:leading-snug font-semibold font-brand tracking-wide uppercase text-blue-surety mb-3">
            {pageData.pageContentKicker}
          </div>
          {pageData?.pageContent?.value && (
            <div className="prose lg:prose-lg prose prose-blue font-brand">
              <StructuredRenderer data={pageData.pageContent} />
            </div>
          )}
        </div>

        <div className="hidden md:grid auto-rows-fr auto-cols-auto md:auto-rows-fr md:gap-10 ml-0 mt-0 md:ml-10 md:mt-20 font-brand flex-1">
          <div className="flex h-full flex-col  justify-items-center text-left w-80 px-5 py-5 border rounded-md">
            <div className="text-xl font-semibold mb-2">
              Electronic Delivery
            </div>
            <div>
              We’ve streamlined the bonding process to make your experience as
              fast and easy as possible.Buy your bond and have it emailed same
              day*
            </div>
          </div>
          <div className="flex h-full flex-col  justify-items-center text-left w-80 px-5 py-5 border rounded-md">
            <div className="text-xl font-semibold mb-2">Best Prices</div>
            <div>
              We shop the top surety markets to find you the best rate.
            </div>
          </div>
          <div className="flex  h-full flex-col  justify-items-center text-left w-80 px-5 py-5 border rounded-md">
            <div className="text-xl font-semibold mb-2">Secure Checkout</div>
            <div>
              Backed by SSL, our online bonding process is secure. We are
              committed to your privacy.
            </div>
          </div>
        </div>
      </div>

      <div className="mt-20">
        <div className="w-full bg-gradient-to-b from-gray-50 to-white border-t">
          <div className="w-full max-w-screen-lg px-4 xl:px-0 mx-auto gap-10 py-20 grid grid-cols-12">
            <div className="md:col-span-5 col-span-full">
              <h2 className="font-brand text-3xl font-bold text-left tracking-tight text-black mb-3">
                Search all contractor bonds in {pageData.state}
              </h2>
              <p className="text-lg text-left space-y-6 mb-6 mt-6">
                Over the years, our bonding experts have put together the most
                comprehensive surety bond library. Most of our contractor
                bonds can be purchased <strong>instantly</strong> online in
                only a <strong>few minutes.</strong>
              </p>
              <p className="relative text-lg  text-left space-y-6 mb-6 mt-6">
                Use the list{' '}
                <span className="hidden md:inline">on the right</span>{' '}
                <span className="inline md:hidden">below</span> to browse all
                of our available contractor bonds in {pageData.state} You can
                also try searching by bond amount, obligee, or bond name.
              </p>
            </div>
            <div className="md:col-span-7 col-span-full">
              <BondTable
                showSearch={props.products.length > 5}
                stateSlug={pageData.slug}
                bonds={props.products}
              ></BondTable>
            </div>
          </div>
        </div>
      </div>
    </div>
  </>;
}

// Get Static Props for /:stateName
export async function getStaticProps({ params: { stateName }, preview }) {
  const siteData = await getSiteData();
  const state = siteData.stateTable[stateName];
  if (!state) {
    return {
      notFound: true,
    };
  }
  const products = state.products.map((p) => siteData.productTable[p]);
  if (preview) {
    state.hub = await getPreviewHubFromDato(stateName);
  }
  return {
    props: {
      pageData: state.hub,
      products,
      stateName,
    },
  };
}

// Get Static Paths from /:stateName
export async function getStaticPaths() {
  const siteData = await getSiteData();
  return {
    paths: Object.keys(siteData.stateTable).map((state) => `/${state}`),
    fallback: false,
  };
}
